import { CurrencyPipe } from '@angular/common';
import { Batch, StatementAnomalyFlag } from '@shared/models/statements/statement.model';
import { RoundUpTwoDecimalsPipe } from '@shared/pipes/round-up-two-decimals.pipe';

export function formatStatementBalance(
  amount: number | null | undefined,
  currencyPipe: CurrencyPipe,
  roundUp: RoundUpTwoDecimalsPipe
): string {
  if (amount === null || amount === undefined) {
    return '-';
  }

  const absolute = Math.abs(roundUp.transform(amount));
  if (amount > 0 && amount >= 0.01) {
    return `${currencyPipe.transform(roundUp.transform(amount), 'CAD', 'symbol-narrow')}`;
  }
  if (amount < 0 && absolute >= 0.01) {
    return `(${currencyPipe.transform(absolute, 'CAD', 'symbol-narrow')})`;
  }
  return '-';
}

export function formatBatchTotal(
  amount: number | null | undefined,
  currencyPipe: CurrencyPipe,
  roundUp: RoundUpTwoDecimalsPipe
): string {
  if (amount === null || amount === undefined) {
    return '-';
  }

  const absolute = Math.abs(roundUp.transform(amount));
  if (amount === 0 || (amount > 0 && amount >= 0.01)) {
    return `${currencyPipe.transform(roundUp.transform(amount), 'CAD', 'symbol-narrow')}`;
  }
  if (amount < 0 && absolute >= 0.01) {
    return `(${currencyPipe.transform(absolute, 'CAD', 'symbol-narrow')})`;
  }
  return '-';
}

export function hasBatchAnomaly(batch: Batch | null | undefined): boolean {
  if (!batch) {
    return false;
  }
  return (batch.anomalyCount ?? 0) > 0
    || !!batch.hasZeroOrNegativeTotal
    || !!batch.varianceLimitReached;
}

export function buildAnomalyTooltipLines(batch: Batch): string[] {
  const lines: string[] = [];

  if (batch.hasZeroOrNegativeTotal) {
    lines.push($localize`:@@anomalyTooltipZeroNegative:Zero or negative batch total`);
  }
  if (batch.varianceLimitReached) {
    lines.push($localize`:@@anomalyTooltipVariance:Customer count exceeds variance threshold`);
  }
  if ((batch.anomalyCount ?? 0) > 0) {
    lines.push($localize`:@@anomalyTooltipLargeBalanceCount:${batch.anomalyCount}:anomalyCount: large balance anomaly(ies) in batch`);
  }

  return lines;
}

export function buildLargeBalanceTooltip(flags: StatementAnomalyFlag[]): string {
  return flags
    .map((flag) => $localize`:@@anomalyTooltipLargeBalance:Balance ${flag.value}:value: exceeds limit ${flag.limit}:limit:`)
    .join('\n');
}

export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
